<template>
  <div class="relative z-50">
    <!-- Mobile Menu Overlay -->
    <div
      v-if="isMenuOpen"
      :class="[
        'curtain max-lg:bg-crosswalk-white bg-white rounded-2xl transform transition-transform ease-in-out duration-200',
        isMenuOpen ? 'scale-y-100 max-md:bg-crosswalk-white' : 'scale-y-0',
      ]"
    >
      <nav role="navigation" aria-label="Main navigation" class="lg:hidden">
        <div
          class="w-full flex flex-col justify-center gap-12 relative pt-24 pb-15 px-10 overflow-auto"
        >
          <div v-for="group in currentHeader" :key="group.id">
            <strong
              class="block text-xs pb-1 text-sand-gray border-b font-normal mb-4"
            >
              {{ group.title }}
            </strong>
            <ul class="flex flex-col gap-2">
              <li
                v-for="link in group.links"
                :key="link.label"
                @click="toggleMenu"
              >
                <a
                  v-if="link.external"
                  :href="link.href"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-lg whitespace-nowrap text-dark-brown flex items-center justify-between"
                >
                  <span>{{ link.label }}</span>
                  <ElementsIconsArrowUp class="w-2 rotate-90" />
                </a>
                <NuxtLink
                  v-else
                  :to="link.href"
                  class="whitespace-nowrap text-lg text-dark-brown flex items-center justify-between"
                  active-class="text-brand font-semibold whitespace-nowrap"
                >
                  <span>{{ link.label }}</span>
                  <ElementsIconsArrowUp class="w-2 rotate-90" />
                </NuxtLink>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </div>

    <!-- Background Overlay -->
    <div
      v-if="isMenuOpen"
      :class="[
        'backlayer fixed inset-0 bg-black/40 z-1 transform transition-opacity ease-in-out duration-200',
        isMenuOpen ? 'opacity-100' : 'opacity-0',
      ]"
    />

    <!-- Header -->
    <header
      :class="[
        'mx-auto flex items-center lg:items-center justify-between max-lg:px-4 max-lg:py-2.5 lg:px-10 lg:pr-4 lg:py-4 rounded-2xl transition-[top] duration-100 top-4 md:top-10 text-action-primary bg-white backdrop-blur-md',
      ]"
      role="banner"
    >
      <div class="flex items-center rounded-2xl group relative">
        <div
          :class="[
            'absolute inset-0 bg-white rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 -mx-10 px-4 py-4 pb-52 group-hover:pointer-events-auto pointer-events-none',
            isScrolled ? '-my-4' : '-my-5.5',
          ]"
        />

        <div
          class="w-20 mr-8 md:mr-16 shrink-0 relative z-10 group-hover:text-dark-brown transition-colors duration-200"
        >
          <NuxtLink to="/">
            <ElementsLogo />
          </NuxtLink>
        </div>

        <nav
          role="navigation"
          aria-label="Main navigation"
          class="hidden lg:block relative z-10"
        >
          <div class="w-full flex relative">
            <div
              v-for="group in currentHeader"
              :key="group.id"
              class="text-sm col-span-1 pr-20 group/nav relative"
            >
              <strong
                class="block font-normal group-hover:text-dark-brown transition-colors duration-200"
              >
                {{ group.title }}
              </strong>
              <ul
                :class="[
                  'flex flex-col gap-3 leading-none absolute top-full transform pt-5 w-max z-20',
                  isMenuOpen
                    ? ''
                    : 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-200',
                ]"
              >
                <li
                  v-for="link in getVisibleLinks(group.links)"
                  :key="link.label"
                >
                  <a
                    v-if="link.external"
                    :href="link.href"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="hover:opacity-50 transition-opacity whitespace-nowrap text-dark-brown"
                  >
                    {{ link.label }}
                  </a>
                  <NuxtLink
                    v-else
                    :to="link.href"
                    class="hover:opacity-50 transition-opacity whitespace-nowrap text-dark-brown"
                    active-class="text-brand font-semibold hover:opacity-50 transition-opacity whitespace-nowrap"
                  >
                    {{ link.label }}
                  </NuxtLink>
                </li>
                <li
                  v-if="hasMoreItems(group.links)"
                  class="flex items-center pt-1"
                >
                  <ElementsIconsMoreItems class="w-5 h-3 text-dark-brown" />
                </li>
              </ul>
            </div>
          </div>
        </nav>
      </div>

      <!-- Danish Language Actions -->
      <div v-if="currentLocale === 'da'" class="flex items-center gap-2">
        <ModulesShopCartButton />
      </div>

      <!-- Non-Danish Language Actions -->
      <div v-else class="flex items-center gap-2">
        <ModulesShopCartButton />
      </div>
    </header>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'

type NavigationLink = {
  label: string
  href: string
  external?: boolean
}

type NavigationGroup = {
  id: string
  title: string
  links: NavigationLink[]
}

// State
const isMenuOpen = ref(false)
const isScrolled = ref(false)

// Navigation data
const NAVIGATION_GROUPS_DE: NavigationGroup[] = []

const NAVIGATION_GROUPS_EN: NavigationGroup[] = []

const NAVIGATION_GROUPS_DA: NavigationGroup[] = [
  {
    id: 'produkter',
    title: 'Produkter',
    links: [
      { label: 'Premium+', href: 'https://sale.saphe.com/' },
      { label: 'Premium', href: 'https://saphe.com/premium' },
      { label: 'Business', href: 'https://saphe.com/business' },
    ],
  },
  {
    id: 'ressourcer',
    title: 'Ressourcer',
    links: [
      { label: 'Saphe T', href: 'https://saphe.com/hardware' },
      { label: 'Funktioner', href: 'https://saphe.com/funktioner' },
    ],
  },
  {
    id: 'support',
    title: 'Support',
    links: [
      {
        label: 'Kontakt os',
        href: 'https://help.saphe.com/hc/da/requests/new',
        external: true,
      },
      // {
      //   label: 'Kom godt igang',
      //   href: 'https://help.saphe.com/hc/da/sections/29924879394589-Kom-godt-i-gang',
      // },
      {
        label: 'Fejlfinding',
        href: 'https://help.saphe.com/hc/da/sections/30910776265117-Fejlfinding',
        external: true,
      },
      {
        label: 'Support',
        href: 'https://help.saphe.com/hc/da',
        external: true,
      },
    ],
  },
]

const headers = {
  de: NAVIGATION_GROUPS_DE,
  en: NAVIGATION_GROUPS_EN,
  da: NAVIGATION_GROUPS_DA,
}

// Composables
const { locale } = useI18n()

// Computed
const currentLocale = computed(() => locale.value)
const currentHeader = computed(() => {
  return headers[currentLocale.value as keyof typeof headers] || headers.da
})

// Methods
const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
}

const getVisibleLinks = (links: NavigationLink[]) => {
  return isMenuOpen.value ? links : links.slice(0, 4)
}

const hasMoreItems = (links: NavigationLink[]) => {
  return !isMenuOpen.value && links.length > 4
}

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50
}

// Body scroll lock effect
watch(isMenuOpen, (newValue) => {
  if (newValue && window.innerWidth < 1024) {
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.width = '100%'
  } else {
    document.body.style.overflow = ''
    document.body.style.position = ''
    document.body.style.width = ''
  }
})

// Lifecycle
onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  // Cleanup body styles
  document.body.style.overflow = ''
  document.body.style.position = ''
  document.body.style.width = ''
})
</script>
